import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { customFuncsApi } from '../../../api'
import DashboardStyle from '../Dashboard'
import { useTranslation } from 'react-i18next'
import { Empty } from 'antd'
import ClipLoader from 'react-spinners/ClipLoader'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import Button from '../../../components/Button'

export default function Tab3() {
	const { t } = useTranslation()

	const [isLoading, setIsLoading] = useState(false)
	const [data, setData] = useState([])
	const [peroid, setPeriod] = useState(dayjs().format('YYYY-MM'))
	const [totalCalculation, setTotalCalculation] = useState({
		monthlyPayment: 0,
		paidForMonth: 0,
		paid: 0,
		beforePayment: 0,
	})

	useEffect(() => {
		setIsLoading(true)
		customFuncsApi
			.get(`xsSql/getPaymentAnalysis`, {
				params: {
					$skip: 0,
					period: peroid || dayjs().format('YYYY-MM'),
				},
				headers: {
					Prefer: 'odata.maxpagesize=20',
				},
			})
			.then((res) => {
				const result = res.data.value
				setData(result)

				const totalMonthlyPayment = res.data.value.reduce(
					(acc, curr) => acc + Number(curr['Oylik_tolov'] || 0),
					0,
				)

				const totalPaidForMonth = res.data.value.reduce(
					(acc, curr) => acc + Number(curr['Оплачено_за_месяц'] || 0),
					0,
				)

				const totalBeforePayment = res.data.value.reduce(
					(acc, curr) => acc + Number(curr['Переплата_за_месяц'] || 0),
					0,
				)

				const totalPaid = totalMonthlyPayment
					? (
							((totalPaidForMonth - totalBeforePayment) * 100) /
							totalMonthlyPayment
						).toFixed(2)
					: 0

				setTotalCalculation({
					monthlyPayment: totalMonthlyPayment.toFixed(2),
					paid: totalPaid,
					paidForMonth: totalPaidForMonth.toFixed(2),
					beforePayment: totalBeforePayment.toFixed(2),
				})
			})

			.finally(() => {
				setIsLoading(false)
			})
	}, [peroid])

	const downloadAsExcel = () => {
		// Prepare headers
		const headers = [
			t('client'),
			t('serialNum'),
			t('dateOfSale'),
			t('installmentPeriod'),
			t('dayToPay'),
			t('monthlyPayment'),
			t('paidForMonth'),
			t('beforePay'),
			t('paid') + ' %',
			'Сумма просрочек',
			t('overdueMonths'),
			'Кумулятивно оплачено',
			'Кумулятивно оплачено (%)',
			t('remaining'),
			t('invoiceAmount'),
			t('phone'),
		]

		// Prepare rows
		const rows = data.map((v) => [
			v['Имя_клиента'],
			v['Серийный_номер'],
			v['Дата_продажи'] ? dayjs(v['Дата_продажи']).format('DD.MM.YYYY') : '--',
			`${v['Срок_рассрочки (мес)']} ${t('month')}`,
			v["To'lashi_kerak_bolgan_kun"]
				? dayjs(v["To'lashi_kerak_bolgan_kun"]).format('DD.MM.YYYY')
				: '--',
			Number(v['Oylik_tolov']).toFixed(2),
			Number(v['Оплачено_за_месяц']).toFixed(2),
			Number(v['Переплата_за_месяц']).toFixed(2),
			(
				(Number(v['Оплачено_за_месяц']) * 100) /
				Number(v['Oylik_tolov'])
			).toFixed(1) + '%',
			Number(v['Сумма_просрочек']).toFixed(2),
			`${v['Просрочено_месяцев']} ${t('month')}`,
			Number(v['Кумулятивно_оплачено']).toFixed(2),
			v['Кумулятивно_оплачено_(%)'] + ' %',
			Number(v['Остаток_сальдо']).toFixed(2),
			Number(v['Сумма_счёта']).toFixed(2),
			v['Телефон'],
		])

		// Add footer row
		rows.push([
			'',
			'',
			'',
			'',
			'',
			Number(totalCalculation.monthlyPayment).toLocaleString(),
			Number(totalCalculation.paidForMonth).toLocaleString(),
			Number(totalCalculation.beforePayment).toLocaleString(),
			totalCalculation.paid + '%',
			'',
			'',
			'',
			'',
			'',
			'',
		])

		// Create worksheet
		const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows])
		const workbook = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Report')

		// Export
		const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
		saveAs(
			new Blob([wbout], { type: 'application/octet-stream' }),
			'Ежемесячные_отчеты.xlsx',
		)
	}

	return (
		<DashboardStyle>
			<div className="container minWidthContainer">
				<p className="productTitle mb-10">{t('monthlyReports')}</p>
				<div className="flex flex-row items-center justify-between">
					<div className="flex flex-col mb-6 max-w-72">
						<p className={'font-medium text-zinc-600 text-sm mb-1'}>
							{t('date')}
						</p>
						<input
							className={
								'border-[1px] border-[#DFE2E9"] p-1 rounded-md w-full flex-1'
							}
							type="month"
							defaultValue={peroid}
							onChange={(e) => setPeriod(e.target.value)}
						/>
					</div>
					<Button
						className="bg-blue-900 text-white h-10 hover:bg-blue-700"
						onClick={downloadAsExcel}
					>
						Скачать как Excel
					</Button>
				</div>
				{isLoading ? (
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<ClipLoader
							loading={true}
							size={20}
							aria-label="Loading Spinner"
							className={'loader'}
							data-testid="loader"
						/>
					</div>
				) : (
					<div className="overflow-y-auto mb-8 max-h-[70vh] relative">
						<table className="w-full text-sm text-left rtl:text-right text-gray-500">
							<thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0 z-10">
								<tr>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('client')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('serialNum')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('dateOfSale')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('installmentPeriod')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('dayToPay')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('monthlyPayment')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('paidForMonth')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('beforePay')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('paid')} %
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										Сумма просрочек
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('overdueMonths')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										Кумулятивно оплачено
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										Кумулятивно оплачено (%)
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('remaining')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('invoiceAmount')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('phone')}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{t('comment')}
									</th>
								</tr>
							</thead>
							<tbody>
								{data.length > 0 ? (
									data.map((v, i) => {
										return (
											<tr
												key={i}
												className="bg-white border-b hover:bg-gray-50"
											>
												<td className="px-6 py-4">{v['Имя_клиента']}</td>
												<td className="px-6 py-4">{v['Серийный_номер']}</td>
												<td className="px-6 py-4">
													{v['Дата_продажи']
														? dayjs(v['Дата_продажи']).format('DD.MM.YYYY')
														: '--'}
												</td>
												<td className="px-6 py-4">
													{v['Срок_рассрочки (мес)']} {t('month')}
												</td>
												<td className="px-6 py-4">
													{v["To'lashi_kerak_bolgan_kun"]
														? dayjs(v["To'lashi_kerak_bolgan_kun"]).format(
																'DD.MM.YYYY',
															)
														: '--'}
												</td>
												<td className="px-6 py-4">
													{Number(v['Oylik_tolov']).toFixed(2)}
												</td>
												<td className="px-6 py-4">
													{Number(v['Оплачено_за_месяц']).toFixed(2)}
												</td>
												<td className="px-6 py-4">
													{Number(v['Переплата_за_месяц']).toFixed(2)}
												</td>

												{/* Oylik tolov ning foyizi (Tolangan - oldindan tolangan) */}
												<td className="px-6 py-4">
													{Number(
														((Number(v['Оплачено_за_месяц']).toFixed(2) -
															Number(v['Переплата_за_месяц']).toFixed(2)) *
															100) /
															Number(v['Oylik_tolov']).toFixed(2),
													).toFixed(1)}
													%
												</td>
												<td className="px-6 py-4">
													{Number(v['Сумма_просрочек']).toFixed(2)}
												</td>
												<td className="px-6 py-4">
													{v['Просрочено_месяцев']} {t('month')}
												</td>
												<td className="px-6 py-4">
													{Number(v['Кумулятивно_оплачено']).toFixed(2)}
												</td>
												<td className="px-6 py-4">
													{v['Кумулятивно_оплачено (%)']} %
												</td>
												<td className="px-6 py-4">
													{Number(v['Остаток_сальдо']).toFixed(2)}
												</td>
												<td className="px-6 py-4">
													{Number(v['Сумма_счёта']).toFixed(2)}
												</td>
												<td className="px-6 py-4">{v['Телефон']}</td>
												<td className="px-6 py-4">
													{v['Комментарий'] || '--'}
												</td>
											</tr>
										)
									})
								) : (
									<tr>
										<td colSpan="15">
											<Empty
												description={t('noInfo')}
												image={Empty.PRESENTED_IMAGE_SIMPLE}
											/>
										</td>
									</tr>
								)}
							</tbody>
							<tfoot className="text-xs text-gray-700 uppercase bg-gray-50 sticky bottom-0 z-10">
								<tr>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{Number(totalCalculation.monthlyPayment).toLocaleString()}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{Number(totalCalculation.paidForMonth).toLocaleString()}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{Number(totalCalculation.beforePayment).toLocaleString()}
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50">
										{totalCalculation.paid}%
									</th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
									<th scope="col" className="px-6 py-4 bg-gray-50"></th>
								</tr>
							</tfoot>
						</table>
					</div>
				)}
			</div>
		</DashboardStyle>
	)
}
